import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle2, Zap, Clock, ShieldCheck, Sparkles } from 'lucide-react';

export default function WhyUs() {
  const [stats, setStats] = useState({
    projects: 0,
    clients: 0,
    satisfaction: 0,
    years: 0
  });

  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  useEffect(() => {
    if (!isInView) return;

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = {
      projects: 150,
      clients: 120,
      satisfaction: 98,
      years: 5
    };

    let step = 0;
    const timer = setInterval(() => {
      step++;
      setStats({
        projects: Math.floor((targets.projects * step) / steps),
        clients: Math.floor((targets.clients * step) / steps),
        satisfaction: Math.floor((targets.satisfaction * step) / steps),
        years: Math.floor((targets.years * step) / steps)
      });

      if (step >= steps) {
        clearInterval(timer);
        setStats(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [isInView]);

  const features = [
    {
      icon: CheckCircle2,
      title: 'Custom-Coded Core',
      description: 'Zero generic page builders. Every platform is hand-crafted line by line to guarantee speed, optimization, and complete layout freedom.',
      color: 'text-[#8B6B4A]',
      bgColor: 'bg-[#8B6B4A]/5'
    },
    {
      icon: Zap,
      title: 'Agile Rhythms',
      description: 'Continuous delivery cycles, bi-weekly review meetings, and live staging links that keep you fully in control of the project lifecycle.',
      color: 'text-[#C8A97E]',
      bgColor: 'bg-[#C8A97E]/5'
    },
    {
      icon: Clock,
      title: 'Punctual Deployment',
      description: 'Detailed roadmap planning, milestone estimates, and strict sprint schedules to ensure your software launches exactly when promised.',
      color: 'text-[#8B6B4A]',
      bgColor: 'bg-[#8B6B4A]/5'
    },
    {
      icon: ShieldCheck,
      title: 'Post-Launch Shield',
      description: 'Comprehensive warranty support, active security auditing, server uptime monitoring, and optional maintenance cycles post-launch.',
      color: 'text-[#C8A97E]',
      bgColor: 'bg-[#C8A97E]/5'
    }
  ];

  const statsData = [
    { label: 'Completed Projects', value: stats.projects, suffix: '+' },
    { label: 'Corporate Clients', value: stats.clients, suffix: '+' },
    { label: 'Satisfaction Rate', value: stats.satisfaction, suffix: '%' },
    { label: 'Years of Experience', value: stats.years, suffix: '+' }
  ];

  return (
    <section id="why-us" ref={sectionRef} className="py-24 md:py-32 relative bg-[#F8F5F0]" data-testid="why-us-section">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 glowing-blob blob-beige opacity-[0.05]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Title */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-[#8B6B4A]/5 border border-[#8B6B4A]/10 rounded-full px-4 py-1.5 backdrop-blur-md mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#C8A97E]" />
            <span className="text-xs font-semibold tracking-wider uppercase text-[#8B6B4A] font-poppins">
              Why Partner With Us
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold font-space text-[#3E2F24] tracking-tight" data-testid="why-us-heading">
            Built for Scale, Designed for Impact
          </h2>
          <p className="text-[#6B625B] text-base sm:text-lg font-poppins max-w-2xl mx-auto mt-4">
            We bridge the gap between creative visual artistry and complex software engineering to deliver state-of-the-art platforms.
          </p>
        </div>

        {/* Counter cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {statsData.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="text-center bg-white/40 border border-[#8B6B4A]/10 rounded-3xl p-6 md:p-8 backdrop-blur-md relative overflow-hidden group hover:border-[#8B6B4A]/30 transition-all duration-300"
              data-testid={`stat-card-${index}`}
            >
              <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#8B6B4A] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div 
                className="text-4xl md:text-5xl font-bold font-space text-transparent bg-clip-text bg-gradient-to-r from-[#3E2F24] via-[#8B6B4A] to-[#C8A97E] mb-2" 
                data-testid={`stat-value-${index}`}
              >
                {stat.value}{stat.suffix}
              </div>
              <p className="text-[#6B625B] font-poppins text-xs font-medium tracking-wide uppercase" data-testid={`stat-label-${index}`}>
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Grid of badges */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
              data-testid={`feature-card-${index}`}
            >
              <div className="bg-white/40 hover:bg-white/80 p-6 rounded-3xl border border-[#8B6B4A]/10 hover:border-[#8B6B4A]/30 transition-all duration-300 h-full flex flex-col justify-between">
                <div>
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${feature.bgColor} ${feature.color} mb-6 group-hover:scale-105 transition-transform duration-300`}>
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold font-space text-[#3E2F24] mb-3" data-testid={`feature-title-${index}`}>
                    {feature.title}
                  </h3>
                  <p className="text-[#6B625B] font-poppins text-xs sm:text-sm leading-relaxed" data-testid={`feature-desc-${index}`}>
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}